// ============================================================
// ByOurConsent.com -- Your Rights Analyzer
// /premium/rights-analyzer
// Premium member tool -- Civic Intelligence Suite
// ============================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { downloadReportAsWord } from '../utils/downloadRtf';

// ============================================================
// Data
// ============================================================
const US_STATES = [
    'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
    'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
    'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
    'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
    'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
    'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
    'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
    'Wisconsin','Wyoming'
];

const SITUATIONS = [
    {
        id: 'traffic_stop',
        icon: '🚗',
        label: 'Traffic stop',
        desc: 'Pulled over by police while driving'
    },
    {
        id: 'search_seizure',
        icon: '🏠',
        label: 'Search or seizure',
        desc: 'Police want to search your home, car, phone, or belongings'
    },
    {
        id: 'arrest',
        icon: '⚖️',
        label: 'Arrest or detention',
        desc: 'Being arrested, detained, or held for questioning'
    },
    {
        id: 'police_encounter',
        icon: '👮',
        label: 'General police encounter',
        desc: 'Police approaching you on the street or in public'
    },
    {
        id: 'employment',
        icon: '💼',
        label: 'Employment — government or public institution',
        desc: 'Rights as an employee of government, school, or public institution'
    },
    {
        id: 'voting',
        icon: '🗳️',
        label: 'Voting rights',
        desc: 'Voter registration, ID requirements, poll access, or ballot issues'
    },
    {
        id: 'assembly',
        icon: '✊',
        label: 'Public assembly or protest',
        desc: 'Attending, organizing, or being arrested at a protest or demonstration'
    },
    {
        id: 'free_speech',
        icon: '📢',
        label: 'Free speech',
        desc: 'Government attempting to restrict or punish speech, expression, or press'
    },
    {
        id: 'recording',
        icon: '📱',
        label: 'Recording police or officials',
        desc: 'Your right to record police, government officials, or public proceedings'
    },
    {
        id: 'immigration',
        icon: '🛂',
        label: 'Immigration enforcement',
        desc: 'ICE encounters, checkpoints, or immigration-related detention'
    },
    {
        id: 'school',
        icon: '🏫',
        label: 'School — student rights',
        desc: 'Student rights regarding speech, searches, discipline, or expression'
    },
    {
        id: 'other',
        icon: '❓',
        label: 'Other situation',
        desc: 'Describe your specific situation in detail'
    },
];

// ============================================================
// Step indicator
// ============================================================
const StepIndicator = ({ step, total, onStepClick }) => (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '2rem' }}>
        {Array.from({ length: total }, (_, i) => (
            <React.Fragment key={i}>
                <div
                    onClick={() => i + 1 < step && onStepClick && onStepClick(i + 1)}
                    title={i + 1 < step ? 'Click to go back' : ''}
                    style={{
                        width: '28px', height: '28px', borderRadius: '50%',
                        background: i + 1 <= step ? '#8b1a1a' : '#ede8dc',
                        border: `0.5px solid ${i + 1 <= step ? '#8b1a1a' : '#c8beaa'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', fontFamily: 'sans-serif', fontWeight: 600,
                        color: i + 1 <= step ? '#f5f0e8' : '#8a8070',
                        cursor: i + 1 < step ? 'pointer' : 'default'
                    }}>
                    {i + 1 < step ? '✓' : i + 1}
                </div>
                {i < total - 1 && (
                    <div style={{ flex: 1, height: '1px', background: i + 1 < step ? '#8b1a1a' : '#c8beaa' }} />
                )}
            </React.Fragment>
        ))}
    </div>
);

// ============================================================
// Styles
// ============================================================
const iStyle = {
    width: '100%', background: '#f5f0e8', border: '0.5px solid #c8beaa',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'sans-serif',
    color: '#1e2a3a', borderRadius: '3px', outline: 'none', boxSizing: 'border-box'
};

const lStyle = {
    fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080',
    letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px'
};

const btnPrimary = {
    background: '#8b1a1a', color: '#f5f0e8', border: 'none',
    padding: '10px 24px', fontSize: '13px', fontFamily: 'sans-serif',
    letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer'
};

const btnSecondary = {
    background: 'transparent', color: '#4a5060', border: '0.5px solid #c8beaa',
    padding: '10px 20px', fontSize: '13px', fontFamily: 'sans-serif',
    borderRadius: '3px', cursor: 'pointer'
};

// ============================================================
// Main component
// ============================================================
const RightsAnalyzerPage = () => {
    const [step,    setStep]    = useState(1);
    const [loading, setLoading] = useState(false);
    const [result,  setResult]  = useState(null);
    const [error,   setError]   = useState('');

    const [form, setForm] = useState({
        situation:   '',
        state:       '',
        description: '',
        questions:   '',
        timing:      'happened',  // happened | happening | planning
    });

    const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

    const selectedSituation = SITUATIONS.find(s => s.id === form.situation);

    const canProceedStep1 = form.situation;
    const canProceedStep2 = form.state && form.description.trim().length > 20;

    // ============================================================
    // Generate rights analysis
    // ============================================================
    const generate = async () => {
        setLoading(true);
        setError('');

        const timingDesc = {
            happened: 'This situation has already occurred',
            happening: 'This situation is currently happening or ongoing',
            planning: 'This situation may occur in the future and the user wants to be prepared'
        }[form.timing];

        const prompt = `You are an expert in constitutional law, civil liberties, and citizens' legal rights. Provide a thorough, accurate analysis of a citizen's rights in a specific situation.

SITUATION: ${selectedSituation?.label}
STATE: ${form.state}
TIMING: ${timingDesc}

DESCRIPTION OF THE SPECIFIC SITUATION:
${form.description}

${form.questions ? `SPECIFIC QUESTIONS FROM THE USER:\n${form.questions}` : ''}

Provide a comprehensive rights analysis with the following sections:

## YOUR RIGHTS IN THIS SITUATION
- State clearly and specifically what constitutional and legal rights apply
- Cite the specific amendments, statutes, or legal standards
- Note any ${form.state}-specific rights that go beyond federal protections
- Be direct — this person needs to know their rights clearly

## WHAT AUTHORITIES CAN LEGALLY DO
- List specifically what police, officials, or employers ARE legally permitted to do in this situation
- Under what conditions or with what legal authority
- What paperwork or process they must follow

## WHAT AUTHORITIES CANNOT LEGALLY DO
- List specifically what is NOT permitted without legal authority, consent, or proper process
- What constitutes an illegal search, unlawful detention, or rights violation
- Any specific ${form.state} state law protections that apply

## WHAT YOU SHOULD DO
- Specific, practical advice for this situation
- What to say (exact language helps — e.g. "I do not consent to searches")
- What NOT to say or do
- How to assert your rights without escalating danger
- When to comply vs when to refuse and document

## HOW TO DOCUMENT
- What to document and how
- Recording laws in ${form.state} (one-party vs two-party consent state)
- What information to collect (badge numbers, names, vehicle numbers, witnesses)
- How to preserve evidence

## IF YOUR RIGHTS ARE VIOLATED
- Immediate steps to take
- Who to contact (civil rights organizations, attorneys, oversight bodies)
- How to file a complaint
- Relevant civil rights organizations in ${form.state}
- Statute of limitations for legal action

## RELEVANT CASE LAW
- Key Supreme Court or ${form.state} state court decisions that establish these rights
- Case names, years, and what they established

## KEY REMINDERS
5 bullet points — the most critical things to remember in this specific situation.

Be specific to ${form.state} law where it differs from federal law. Be practical — this person may be in or about to be in a stressful situation and needs clear, actionable guidance, not academic discussion. If rights vary based on circumstances, explain those conditions clearly.`;

        try {
            const response = await fetch('/api/ai/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('boc_token')}`
                },
                body: JSON.stringify({ prompt, max_tokens: 3000 })
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Generation failed');
            const text = data.text || '';
            if (!text) throw new Error('No response generated');

            setResult({ analysis: text, situation: selectedSituation?.label, state: form.state });
            setStep(3);
        } catch (err) {
            setError('Failed to generate analysis. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // Download
    // ============================================================
    const downloadWord = () => {
        downloadReportAsWord(
            result.analysis,
            `Your-Rights-${result.situation?.replace(/[^a-z0-9]/gi,'-').slice(0,30)}-${result.state}`
        );
    };

    const reset = () => {
        setStep(1); setResult(null); setError('');
        setForm({ situation: '', state: '', description: '', questions: '', timing: 'happened' });
    };

    // ============================================================
    // Render
    // ============================================================
    return (
        <>
            <Helmet><title>Your Rights Analyzer — By Our Consent</title></Helmet>
            <Nav />

            {/* Header */}
            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '1.5rem' }}>
                <div style={{ maxWidth: '720px', margin: '0 auto' }}>
                    <Link to="/premium" style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a9aaa', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        ← Premium tools
                    </Link>
                    <div style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8b9a7a', marginTop: '10px' }}>
                        Civic intelligence suite
                    </div>
                    <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginTop: '4px' }}>
                        Your Rights Analyzer
                    </h1>
                    <p style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a9aaa', marginTop: '6px' }}>
                        Know exactly what your rights are in any specific situation — what authorities
                        can and cannot legally do, what to say, how to document, and who to call
                        if your rights are violated.
                    </p>
                </div>
            </div>

            <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2rem 1.5rem' }}>

                <StepIndicator step={step} total={3} onStepClick={setStep} />

                {/* ── STEP 1: Situation ── */}
                {step === 1 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 1 — What is your situation?
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '1.5rem' }}>
                            {SITUATIONS.map(s => (
                                <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: form.situation === s.id ? '#f0e8e0' : '#f5f0e8', border: `0.5px solid ${form.situation === s.id ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', cursor: 'pointer' }}>
                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ fontSize: '20px', flexShrink: 0 }}>{s.icon}</span>
                                        <div>
                                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: form.situation === s.id ? 600 : 400 }}>{s.label}</div>
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '1px' }}>{s.desc}</div>
                                        </div>
                                    </div>
                                    <input type="radio" name="situation" checked={form.situation === s.id}
                                        onChange={() => set('situation', s.id)}
                                        style={{ width: '16px', height: '16px', accentColor: '#8b1a1a', flexShrink: 0 }} />
                                </label>
                            ))}
                        </div>

                        <button style={{ ...btnPrimary, opacity: !canProceedStep1 ? 0.5 : 1 }}
                            disabled={!canProceedStep1}
                            onClick={() => setStep(2)}>
                            Next: Describe your situation →
                        </button>
                    </div>
                )}

                {/* ── STEP 2: Details ── */}
                {step === 2 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 2 — Describe your situation
                        </div>

                        {/* Help panel */}
                        <details style={{ marginBottom: '1.5rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px' }}>
                            <summary style={{ padding: '10px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#8b6a3a', fontWeight: 600, cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                💡 How to get the best results
                            </summary>
                            <div style={{ padding: '0 14px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.7 }}>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px', marginTop: '10px' }}>Describe the situation — include who, what, where</div>
                                <div style={{ marginBottom: '6px' }}>
                                    The more specific your description, the more accurate and useful the rights analysis.
                                    Include who is involved, what they did or said, and where it happened.
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '4px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#8b1a1a' }}>Too vague:</strong> "A police officer stopped me and searched my car."
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#3b6d11' }}>Much better:</strong> "I was pulled over for a broken tail light. After issuing a warning,
                                    the officer asked if he could search my car. I said no. He said he was going to call a drug dog
                                    and that I had to wait. I was not given a ticket and had not violated any laws."
                                </div>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>Specific questions — ask exactly what you need to know</div>
                                <div style={{ marginBottom: '6px' }}>
                                    If you have a specific concern or question beyond the general situation, ask it directly.
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "Was the officer legally allowed to make me wait for a drug dog
                                    after the stop was already complete? Can I record the encounter on my phone in Virginia?"
                                </div>
                            </div>
                        </details>

                        {/* Situation summary */}
                        <div style={{ background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '10px 14px', marginBottom: '1.25rem', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '18px' }}>{selectedSituation?.icon}</span>
                            <strong style={{ color: '#1e2a3a' }}>{selectedSituation?.label}</strong>
                        </div>

                        {/* Timing */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>When is this situation occurring?</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {[
                                    ['happened', 'Already happened'],
                                    ['happening', 'Currently happening'],
                                    ['planning', 'Planning / preparing']
                                ].map(([val, label]) => (
                                    <button key={val} onClick={() => set('timing', val)}
                                        style={{ flex: 1, padding: '8px', background: form.timing === val ? '#8b1a1a' : '#f5f0e8', color: form.timing === val ? '#f5f0e8' : '#4a5060', border: `0.5px solid ${form.timing === val ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', fontSize: '12px', fontFamily: 'sans-serif', cursor: 'pointer' }}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* State */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>State where this occurred or will occur *</label>
                            <select style={iStyle} value={form.state} onChange={e => set('state', e.target.value)}>
                                <option value="">Select state...</option>
                                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>

                        {/* Description */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Describe the specific situation *</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={5}
                                placeholder="Describe what happened or what you expect to happen. Include who is involved, what was said or done, and any relevant details..."
                                value={form.description}
                                onChange={e => set('description', e.target.value)} />
                        </div>

                        {/* Specific questions */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={lStyle}>Specific questions you want answered (optional)</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3}
                                placeholder="Any specific questions beyond the general situation? e.g. Can I record this? Do I have to give my name? Can they detain me?..."
                                value={form.questions}
                                onChange={e => set('questions', e.target.value)} />
                        </div>

                        {error && (
                            <div style={{ background: '#fdf0ee', border: '0.5px solid #d4a8a0', borderRadius: '3px', padding: '10px 12px', fontSize: '13px', fontFamily: 'sans-serif', color: '#8b1a1a', marginBottom: '1rem' }}>
                                {error}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button style={btnSecondary} onClick={() => setStep(1)}>← Back</button>
                            <button style={{ ...btnPrimary, opacity: !canProceedStep2 || loading ? 0.6 : 1 }}
                                disabled={!canProceedStep2 || loading}
                                onClick={generate}>
                                {loading ? 'Analyzing your rights...' : 'Analyze My Rights →'}
                            </button>
                        </div>

                        {loading && (
                            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', lineHeight: 1.6 }}>
                                ⏳ Researching your rights, {form.state} state law, and relevant case law...
                                <br />This may take 20-30 seconds for a thorough analysis.
                            </div>
                        )}
                    </div>
                )}

                {/* ── STEP 3: Result ── */}
                {step === 3 && result && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                                <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                                    Your rights analysis
                                </div>
                                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '4px' }}>
                                    {result.situation} · {result.state}
                                </div>
                            </div>
                            <button onClick={downloadWord} style={btnPrimary}>
                                ⬇ Download Report
                            </button>
                        </div>

                        {/* Analysis display */}
                        <div style={{ background: '#fff', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '2rem', fontFamily: 'Georgia, serif', fontSize: '13px', lineHeight: 1.8, color: '#1a1a1a', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            {result.analysis.split('\n').map((line, i) => {
                                if (line.startsWith('## ')) {
                                    return (
                                        <div key={i} style={{ fontSize: '15px', fontWeight: 600, color: '#1e2a3a', fontFamily: 'sans-serif', marginTop: i === 0 ? 0 : '1.5rem', marginBottom: '8px', paddingBottom: '6px', borderBottom: '0.5px solid #c8beaa' }}>
                                            {line.replace('## ', '')}
                                        </div>
                                    );
                                }
                                if (line.startsWith('- ') || line.startsWith('• ')) {
                                    return <div key={i} style={{ paddingLeft: '1rem', marginBottom: '4px' }}>• {line.slice(2)}</div>;
                                }
                                if (!line.trim()) return <div key={i} style={{ height: '8px' }} />;
                                return <div key={i} style={{ marginBottom: '4px' }}>{line}</div>;
                            })}
                        </div>

                        {/* Actions */}
                        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button onClick={downloadWord} style={btnPrimary}>⬇ Download Report</button>
                            <button onClick={reset} style={btnSecondary}>Analyze another situation</button>
                        </div>

                        {/* Disclaimer */}
                        <div style={{ marginTop: '1.5rem', padding: '10px 14px', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', lineHeight: 1.6 }}>
                            This analysis is for educational purposes and is not legal advice. Rights vary by specific
                            circumstances, and laws change. If you are facing a serious legal situation, contact a
                            qualified civil rights attorney. In an emergency, prioritize your safety.
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </>
    );
};

export default RightsAnalyzerPage;
